import React, { useEffect, useMemo, useState,useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Area, AreaChart, RadialBar, RadialBarChart, PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Colors for charts
const COLORS = ['#272757', '#20B2AA', '#f59e0b', '#4A0404', '#10b981', '#6366f1', '#f97316', '#8b5cf6', '#06b6d4', '#14b8a6'];

// Month maps and helpers (copied/adapted)
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = MONTH_NAMES.map(m => m.slice(0,3).toUpperCase());
const MONTH_MAP = {
  'JANUARY': 0,'JAN':0,'FEBRUARY':1,'FEB':1,'MARCH':2,'MAR':2,'APRIL':3,'APR':3,
  'MAY':4,'JUNE':5,'JUN':5,'JULY':6,'JUL':6,'AUGUST':7,'AUG':7,'SEPTEMBER':8,'SEP':8,'SEPT':8,
  'OCTOBER':9,'OCT':9,'NOVEMBER':10,'NOV':10,'DECEMBER':11,'DEC':11,'DEC':11
};

function parseMonthIndexFromEnrolledBatch_ExactFormat(raw) {
  if (!raw && raw !== 0) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const tokens = s.split(/[-\s\/]+/).filter(Boolean);
  if (tokens.length < 2) return null;
  let monthToken = null;
  if (tokens.length === 3) monthToken = tokens[1];
  else monthToken = tokens.find(t => MONTH_MAP.hasOwnProperty(String(t).toUpperCase()));
  if (!monthToken) return null;
  const idx = MONTH_MAP[String(monthToken).toUpperCase()];
  return typeof idx === 'number' ? idx : null;
}

function parseYearFromEnrolledBatch(raw) {
  if (!raw) return null;
  const s = String(raw);
  const m = s.match(/\b(20\d{2})\b/);
  if (m) return parseInt(m[1], 10);
  // fallback to ISO date
  const asDate = new Date(s);
  if (!isNaN(asDate)) return asDate.getFullYear();
  return null;
}

function parseAmount(v) {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let s = String(v).trim();
  s = s.replace(/[₹,$\s]/g, '').replace(/,/g, '');
  s = s.replace(/[^0-9.\-]/g, '');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function formatDateForCsv(v) {
  if (!v) return '';
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().split('T')[0];
  // if already in format like "13-NOVEMBER-2025" try to normalize
  return String(v);
}

function DashboardEnrollemnts() {
  const navigate = useNavigate();
  const location = useLocation();

      const dashboardRef = useRef(null); // attach to container to capture to dowmload pdf,png
  

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  // lead states
  const [students, setStudents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters (same style as Pending)
  const [filterMonth, setFilterMonth] = useState('ALL'); // 'ALL' or 0..11
  const [filterYear, setFilterYear] = useState('ALL');   // 'ALL' or 2025 etc.
  const [filterRole, setFilterRole] = useState('ALL');   // 'ALL' | 'STUDENT' | 'PROFESSIONAL'
  const [filterFirstInstallment, setFilterFirstInstallment] = useState('ALL'); // 'ALL'|'PAID'|'UNPAID'
  const [q, setQ] = useState(''); // free-text search across name/email/phone

  // verify user effect
  useEffect(() => {
    const verify = async () => {
      if (!token) return navigate('/login');
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/check-user`, { email, role, token });
        if (!res?.data?.success) {
          toast.error(res?.data?.message || 'Please login again');
          localStorage.clear();
          return navigate('/login');
        }
      } catch (err) {
        toast.error('Please login again');
        localStorage.clear();
        navigate('/login');
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname]);

  // fetch leads (combined)
  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const [sRes, pRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/enrollment/all-student-enrolled-leads`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}/enrollment/all-professional-enrolled-leads`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const sData = Array.isArray(sRes.data) ? sRes.data : (Array.isArray(sRes.data?.data) ? sRes.data.data : []);
        const pData = Array.isArray(pRes.data) ? pRes.data : (Array.isArray(pRes.data?.data) ? pRes.data.data : []);

        setStudents(sData.map((r, i) => ({ key: r.Email ?? r.email ?? r._id ?? `s-${i}`, ...r })));
        setProfessionals(pData.map((r, i) => ({ key: r.Email ?? r.email ?? r._id ?? `p-${i}`, ...r })));
      } catch (err) {
        console.error(err);
        setError('Failed to fetch leads');
        toast.error('Failed to load leads');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [token, location.key]);

  // Combined array helper
  const combinedLeads = useMemo(() => [...students, ...professionals], [students, professionals]);

  // derive available years from combinedLeads for the year dropdown
  const availableYears = useMemo(() => {
    const sety = new Set();
    combinedLeads.forEach(r => {
      const y = parseYearFromEnrolledBatch(r.RegisteredBatch ?? r.Registered_Batch ?? r.RegisteredBatch ?? r.RegisteredBatchName ?? r.RegisteredBatch);
      if (y) sety.add(y);
      else {
        const possibleDates = [r.LeadDate, r.leadDate, r.createdAt, r.created_at, r.created];
        for (const pd of possibleDates) {
          if (!pd) continue;
          const d = new Date(pd);
          if (!isNaN(d)) { sety.add(d.getFullYear()); break; }
        }
      }
    });
    return Array.from(sety).sort((a,b) => b-a);
  }, [combinedLeads]);

  // classifyLead helper (simple)
  const classifyLead = (r) => {
    if (!r) return 'professional';
    const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim().toUpperCase());
    const fields = [r.LeadCategory, r.category, r.Type, r.type, r.role, r.Role, r.leadType, r.LeadType, r.EnrollmentType, r.enrollmentType];
    const joined = fields.map(toStr).join(' ');
    if (joined.includes('STUDENT') || joined.includes('LEARNER') || joined.includes('FRESHER')) return 'student';
    return 'professional';
  };

  // filteredLeads pipeline (applies filters same as Pending)
  const filteredLeads = useMemo(() => {
    const qLower = (q || '').trim().toLowerCase();

    const getMonthYear = (r) => {
      const raw = r.RegisteredBatch ?? r.Registered_Batch ?? r.RegisteredBatchName ?? r.RegisteredBatch ?? r.LeadDate ?? r.leadDate ?? '';
      const monthIdx = parseMonthIndexFromEnrolledBatch_ExactFormat(raw);
      let year = parseYearFromEnrolledBatch(raw);
      if (!year) {
        const created = r.LeadDate ?? r.leadDate ?? r.createdAt ?? r.created_at ?? r.created;
        if (created) {
          const d = new Date(created);
          if (!isNaN(d)) year = d.getFullYear();
        }
      }
      return { monthIdx, year };
    };

    return combinedLeads.filter((r) => {
      // MONTH filter
      if (filterMonth !== 'ALL') {
        const { monthIdx } = getMonthYear(r);
        if (monthIdx !== filterMonth) return false;
      }

      // YEAR filter
      if (filterYear !== 'ALL') {
        const { year } = getMonthYear(r);
        if (year !== filterYear) return false;
      }

      // ROLE filter
      if (filterRole !== 'ALL') {
        const cls = classifyLead(r);
        if (filterRole === 'STUDENT' && cls !== 'student') return false;
        if (filterRole === 'PROFESSIONAL' && cls !== 'professional') return false;
      }

      // FIRST INSTALLMENT filter
      if (filterFirstInstallment !== 'ALL') {
        const raw = String(r.FirstInstallmentStatus ?? r.firstInstallmentStatus ?? r.first_installment_status ?? r.FirstInstallmentAmount ?? r.firstInstallmentAmount ?? '').trim().toUpperCase();
        const isPaid = /\bPAID\b/.test(raw) || (parseAmount(r.FirstInstallmentAmount ?? r.firstInstallmentAmount ?? r.first_installment_amount) > 0);
        const isUnpaid = !isPaid;
        if (filterFirstInstallment === 'PAID' && !isPaid) return false;
        if (filterFirstInstallment === 'UNPAID' && !isUnpaid) return false;
      }

      // TEXT search across a few common fields
      if (qLower) {
        const hay = [
          r.FullName ?? r.fullName ?? r.name ?? '',
          r.Email ?? r.email ?? '',
          r.PhoneNumber ?? r.Phone ?? r.phone ?? r.mobile ?? '',
          r.LeadSource ?? r.leadSource ?? r.source ?? ''
        ].join(' ').toLowerCase();
        if (!hay.includes(qLower)) return false;
      }

      return true;
    });
  }, [combinedLeads, filterMonth, filterYear, filterRole, filterFirstInstallment, q]);

  // Other derived chart datasets (reuse existing logic)
  const totalPending = filteredLeads.length;

  const topSources = useMemo(() => {
    const counts = {};
    const normalize = (v) => (v || '').toString().trim().toUpperCase();
    filteredLeads.forEach((r) => {
      const raw = r.LeadSource || r.leadSource || r.source || r.Source || r.lead_source || '';
      const key = normalize(raw) || 'UNKNOWN';
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([name, cnt]) => ({ name, count: cnt })).sort((a,b) => b.count - a.count).slice(0,10);
  }, [filteredLeads]);

  const typeBreakdown = useMemo(() => ([
    { name: 'Students', value: filteredLeads.filter(l => classifyLead(l) === 'student').length },
    { name: 'Professionals', value: filteredLeads.filter(l => classifyLead(l) === 'professional').length },
  ]), [filteredLeads]);

  // Enrollment type pie
  const enrollmentTypeData = useMemo(() => {
    const counts = { 'JUNIOR PENETRATION TESTER': 0, 'SENIOR PENETRATION TESTER': 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || '').toString().trim().toUpperCase();
      if (raw.includes('JUNIOR')) counts['JUNIOR PENETRATION TESTER'] += 1;
      else if (raw.includes('SENIOR')) counts['SENIOR PENETRATION TESTER'] += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredLeads]);

  // Revenue totals (negotiated / paid)
  const revenueTotals = useMemo(() => {
    const totals = { negotiated: 0, paid: 0, byType: { 'JUNIOR PENETRATION TESTER': { negotiated: 0, paid: 0 }, 'SENIOR PENETRATION TESTER': { negotiated: 0, paid: 0 } } };
    const parseNum = (v) => {
      const n = parseFloat(String(v ?? 0).replace(/[,₹\s]/g, ''));
      return Number.isFinite(n) ? n : 0;
    };
    filteredLeads.forEach((r) => {
      const rawType = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || '').toString().trim().toUpperCase();
      const type = rawType.includes('JUNIOR') ? 'JUNIOR PENETRATION TESTER' : rawType.includes('SENIOR') ? 'SENIOR PENETRATION TESTER' : null;
      const negotiated = parseNum(r.NegotiatedFee ?? r.negotiatedFee ?? r.negotiated_fee ?? 0);
      const first = parseNum(r.FirstInstallmentAmount ?? r.firstInstallmentAmount ?? r.first_installment_amount ?? 0);
      const second = parseNum(r.SecondInstallmentPaidAmount ?? r.secondInstallmentPaidAmount ?? r.second_installment_paid_amount ?? 0);
      const paid = first + second;
      totals.negotiated += negotiated;
      totals.paid += paid;
      if (type && totals.byType[type]) {
        totals.byType[type].negotiated += negotiated;
        totals.byType[type].paid += paid;
      }
    });
    return totals;
  }, [filteredLeads]);

  const revenueChartData = useMemo(() => ([
    { name: 'Negotiated (Total)', value: revenueTotals.negotiated },
    { name: 'Actual Paid (Total)', value: revenueTotals.paid },
    { name: 'Junior — Paid', value: revenueTotals.byType['JUNIOR PENETRATION TESTER'].paid },
    { name: 'Senior — Paid', value: revenueTotals.byType['SENIOR PENETRATION TESTER'].paid },
  ]), [revenueTotals]);

  const formatCurrency = (n) => {
    try { return (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch { return String(n); }
  };

  // ---------- CSV export ----------
  const CSV_FIELDS = [
    { key: 'FullName', label: 'FullName' },
    { key: 'Email', label: 'Email' },
    { key: 'PhoneNumber', label: 'PhoneNumber' },
    { key: 'LeadCategory', label: 'LeadCategory' },
    { key: 'LeadDate', label: 'LeadDate' },
    { key: 'LeadSource', label: 'LeadSource' },
    { key: 'EnrollmentType', label: 'EnrollmentType' },
    { key: 'RegisteredBatch', label: 'RegisteredBatch' },
    { key: 'BatchDay', label: 'BatchDay' },
    { key: 'NegotiatedFee', label: 'NegotiatedFee' },
    { key: 'FirstInstallmentAmount', label: 'FirstInstallmentAmount' },
    { key: 'FirstInstallmentDate', label: 'FirstInstallmentDate' },
    { key: 'SecondInstallmentPaidAmount', label: 'SecondInstallmentPaidAmount' },
    { key: 'SecondInstallmentDate', label: 'SecondInstallmentDate' },
  ];

  function mapLeadToCsvRow(r) {
    return {
      FullName: r.FullName ?? r.fullName ?? r.name ?? '',
      Email: r.Email ?? r.email ?? '',
      PhoneNumber: r.PhoneNumber ?? r.Phone ?? r.phone ?? r.mobile ?? '',
      LeadCategory: r.LeadCategory ?? r.leadCategory ?? r.category ?? '',
      LeadDate: formatDateForCsv(r.LeadDate ?? r.leadDate ?? r.createdAt ?? r.created_at ?? ''),
      LeadSource: r.LeadSource ?? r.leadSource ?? r.source ?? '',
      EnrollmentType: r.EnrollmentType ?? r.enrollmentType ?? r.enrollment_type ?? '',
      RegisteredBatch: r.RegisteredBatch ?? r.Registered_Batch ?? r.RegisteredBatchName ?? '',
      BatchDay: r.BatchDay ?? r.batchDay ?? r.batch_day ?? r.Batch ?? '',
      NegotiatedFee: parseAmount(r.NegotiatedFee ?? r.negotiatedFee ?? r.negotiated_fee ?? r.TotalFee ?? 0),
      FirstInstallmentAmount: parseAmount(r.FirstInstallmentAmount ?? r.firstInstallmentAmount ?? r.first_installment_amount ?? 0),
      FirstInstallmentDate: formatDateForCsv(r.FirstInstallmentDate ?? r.firstInstallmentDate ?? r.first_installment_date ?? ''),
      SecondInstallmentPaidAmount: parseAmount(r.SecondInstallmentPaidAmount ?? r.secondInstallmentPaidAmount ?? r.second_installment_paid_amount ?? 0),
      SecondInstallmentDate: formatDateForCsv(r.SecondInstallmentDate ?? r.secondInstallmentDate ?? r.second_installment_date ?? ''),
    };
  }

  const downloadCsv = (rows, filename = 'enrollments.csv') => {
    if (!rows || !rows.length) {
      toast.info('No rows to export');
      return;
    }
    // create CSV header
    const header = CSV_FIELDS.map(f => f.label).join(',');
    // create CSV body, escape quotes
    const body = rows.map(row => CSV_FIELDS.map(f => {
      const v = row[f.key];
      if (v === null || v === undefined) return '';
      const s = String(v);
      // escape quotes by doubling them, wrap field in quotes if contains comma/newline/quote
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    }).join(',')).join('\n');

    // Add BOM for Excel compatibility and build blob
    const csvContent = '\uFEFF' + header + '\n' + body;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportFilteredCsv = () => {
    const rows = filteredLeads.map(mapLeadToCsvRow);
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(rows, `enrollments_${ts}.csv`);
  };

    const normalizeVal = (v) => (v || '').toString().trim().toUpperCase();

  const aggregateBatchDistribution = (arr) => {
    // initialize for both expected enrollment types so charts always show both rows
    const types = [
      'JUNIOR PENETRATION TESTER',
      'SENIOR PENETRATION TESTER'
    ];

    const result = types.map((t) => ({ enrollmentType: t, MORNING: 0, EVENING: 0 }));

    arr.forEach((r) => {
      const eType = normalizeVal(r.EnrollmentType || r.enrollmentType || r.EnrollemntType || r.enrollment_type) || 'UNKNOWN';
      const batch = normalizeVal(r.BatchDay || r.batchDay || r.batch_day || r.Batch || r.batch) || 'UNKNOWN';

      // find index for known types; ignore unknown types but you can adapt if needed
      const idx = result.findIndex(x => x.enrollmentType === eType);
      if (idx === -1) return; // skip types we don't want to show
      if (batch.includes('MORN')) result[idx].MORNING += 1;
      else if (batch.includes('EVEN')) result[idx].EVENING += 1;
      // if you have other batch labels, you can extend here (e.g., 'AFTERNOON')
    });

    return result;
  };

  const studentsBatchDist = useMemo(() => aggregateBatchDistribution(students), [students]);
  const professionalsBatchDist = useMemo(() => aggregateBatchDistribution(professionals), [professionals]);


   // Export PNG
    const exportFilteredLeadsPNG = async () => {
      if (!dashboardRef.current) {
        toast.error('Dashboard not ready to export');
        return;
      }
      try {
        // scale for better resolution
        const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `enrollments_${(new Date()).toISOString().slice(0,10)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('PNG exported');
      } catch (err) {
        console.error(err);
        toast.error('Failed to export PNG');
      }
    };
    
    // Export PDF (multi-page aware)
    const exportFilteredLeadsPDF = async () => {
      if (!dashboardRef.current) {
        toast.error('Dashboard not ready to export');
        return;
      }
      try {
        // render to canvas at higher scale
        const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
        const imgData = canvas.toDataURL('image/png');
    
        // A4 size in pt (points) for jsPDF
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
    
        // compute image dims to fit page width
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
        if (imgHeight <= pageHeight) {
          // single page
          pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        } else {
          // multi-page: split vertically
          let remainingHeight = imgHeight;
          let position = 0;
          const canvasPageHeight = (canvas.height / imgHeight) * pageHeight; // approximate page height in canvas px
    
          // We'll slice the full-size canvas into pages by drawing portions to a temporary canvas
          const renderedCanvas = canvas;
          const scale = imgWidth / canvas.width; // scale factor used for img in PDF
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
    
          const pageCanvasHeightPx = Math.floor(pageHeight / scale); // how many source px fit per pdf page
    
          pageCanvas.width = canvas.width;
          pageCanvas.height = pageCanvasHeightPx;
    
          let srcY = 0;
          while (srcY < renderedCanvas.height) {
            // draw portion
            pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
            pageCtx.drawImage(renderedCanvas, 0, srcY, renderedCanvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);
            const pageDataUrl = pageCanvas.toDataURL('image/png');
    
            if (srcY > 0) pdf.addPage();
            pdf.addImage(pageDataUrl, 'PNG', 0, 0, imgWidth, (pageCanvas.height * imgWidth) / pageCanvas.width);
    
            srcY += pageCanvas.height;
          }
        }
    
        const fileName = `enrollments_${(new Date()).toISOString().slice(0,10)}.pdf`;
        pdf.save(fileName);
        toast.success('PDF exported');
      } catch (err) {
        console.error(err);
        toast.error('Failed to export PDF');
      }
    };

  // ---------- JSX ----------
  return (
       <div ref={dashboardRef} className="p-6 space-y-6">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      {/* --- Filters row (month, year, role, first-installment, search, export) --- */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All months</option>
          {MONTH_NAMES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
        </select>

        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All years</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All roles</option>
          <option value="STUDENT">Students</option>
          <option value="PROFESSIONAL">Professionals</option>
        </select>

        <select value={filterFirstInstallment} onChange={(e) => setFilterFirstInstallment(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All payments</option>
          <option value="PAID">Paid</option>
          <option value="UNPAID">Unpaid</option>
        </select>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email/phone" className="px-3 py-2 border rounded flex-1 min-w-[220px]" />

        <button onClick={() => { setFilterMonth('ALL'); setFilterYear('ALL'); setFilterRole('ALL'); setFilterFirstInstallment('ALL'); setQ(''); }} className="px-3 py-2 rounded bg-slate-100">Reset</button>

        <div className="flex items-center gap-2 ml-auto">
          <button onClick={handleExportFilteredCsv} className="px-3 py-2 text-white rounded-md shadow-sm bg-emerald-600 hover:bg-emerald-700">
            Export CSV
          </button>

            {/* PNG export */}
<button onClick={exportFilteredLeadsPNG} className="px-3 py-2 ml-2 text-white rounded bg-sky-600 hover:bg-sky-700">Export PNG</button>

{/* PDF export */}
<button onClick={exportFilteredLeadsPDF} className="px-3 py-2 ml-2 text-white rounded bg-rose-600 hover:bg-rose-700">Export PDF</button>
      
     
         
        </div>
      </div>

      {/* Top summary & sources */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="p-4 bg-white shadow rounded-xl min-h-[120px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">TOTAL ENROLLMENTS</h3>
          </div>
          <div className="mt-4 text-center">
            <div className="font-bold text-7xl">{loading ? '...' : totalPending}</div>
          </div>
        </div>

        <div className="p-4 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">LEADS SOURCES</h3>
            <button className="text-xl font-bold text-black" onClick={() => window.location.reload()}>↻</button>
          </div>
          <div className="divide-y">
            {topSources.length === 0 && <div className="p-6 text-sm text-slate-400">No sources available</div>}
            {topSources.map((s, i) => (
              <div key={s.name} className="flex items-center justify-between py-3">
                <div className="text-sm font-medium text-black">{i + 1}. {s.name}</div>
                <div className="text-sm font-medium text-black">{s.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts & Admin revenue */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-lg font-semibold text-black">ENROLLMENT TYPE</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={enrollmentTypeData} dataKey="value" nameKey="name" innerRadius={10} outerRadius={120} paddingAngle={2} label={(e) => `${e.name} (${e.value})`}>
                  {enrollmentTypeData.map((entry, idx) => {
                    let color = '#94a3b8';
                    if (entry.name.includes('JUNIOR')) color = '#1e3a8a';
                    if (entry.name.includes('SENIOR')) color = '#064e3b';
                    return <Cell key={`cell-${idx}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

{ role === 'ADMIN' && (

  <>
              {/* ===== Revenue summary ===== */}
<div className="p-4 bg-white shadow rounded-xl">
  <div className="flex items-center justify-between mb-2">
    <h3 className="text-lg font-semibold text-black">TOTAL REVENUE</h3>
    <button className="text-sm text-slate-500" onClick={() => window.location.reload()}>↻</button>
  </div>

  <div className="grid items-center grid-cols-1 gap-4">


   <div className="grid grid-cols-1 gap-6 text-center sm:grid-cols-3">
  {/* Column 1 — Negotiated Fee */}
  <div className="p-4 rounded-lg shadow-sm bg-slate-50">
    <div className="text-sm text-slate-500">Negotiated Fee (Combined)</div>
    <div className="mt-2 text-2xl font-bold text-black">
      ₹ {formatCurrency(revenueTotals.negotiated)}
    </div>
  </div>

  {/* Column 2 — Actual Paid */}
  <div className="p-4 rounded-lg shadow-sm bg-slate-50">
    <div className="text-sm text-slate-500">Actual Paid (First + Second)</div>
    <div className="mt-2 text-2xl font-bold text-black">
      ₹ {formatCurrency(revenueTotals.paid)}
    </div>
  </div> 

  {/* Column 3 — Breakdown by Type */}
  <div className="p-4 rounded-lg shadow-sm bg-slate-50">
    <div className="text-sm text-slate-500">Breakdown by Type</div>
    <div className="mt-2 space-y-1 text-sm text-black">
      <div>
        <span className="font-semibold">Junior:</span>{' '}
        ₹ {formatCurrency(revenueTotals.byType['JUNIOR PENETRATION TESTER'].paid)}
      </div>
      <div>
        <span className="font-semibold">Senior:</span>{' '}
        ₹ {formatCurrency(revenueTotals.byType['SENIOR PENETRATION TESTER'].paid)}
      </div>
    </div>
  </div>
</div> 


    <div style={{ width: '100%', height: 190 }}>
      <ResponsiveContainer>
        <BarChart data={revenueChartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <XAxis dataKey="name" tick={{fontSize: 10}} />
          <YAxis />
          <Tooltip formatter={(val) => `₹ ${formatCurrency(val)}`} />
          <Bar dataKey="value" barSize={60}
          label={{
                position: "top",
                formatter: (v) => `₹${v.toLocaleString()}`,
                fill: "#111827",
                fontSize: 14,
                fontWeight: 600,
              }}
          >
            {revenueChartData.map((entry, idx) => (
              <Cell key={`rev-${idx}`} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
</div>

</>
)}
{/* ===== end Revenue summary ===== */}

       
      </div>

      {/* ============ New UI Block: BATCH DAY DISTRIBUTION ============ */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Students Batch Day Distribution */}
        <div className="p-4 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">BATCH DAY DISTRIBUTION — STUDENTS</h3>
            <div className="text-sm text-slate-500">EnrollmentType: Junior / Senior</div>
          </div>

          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart
                data={studentsBatchDist}
                margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="enrollmentType" tick={{fontSize:12}} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="MORNING" name="Morning" barSize={20}>
                  {studentsBatchDist.map((entry, idx) => (
                    <Cell key={`s-m-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
                <Bar dataKey="EVENING" name="Evening" barSize={20}>
                  {studentsBatchDist.map((entry, idx) => (
                    <Cell key={`s-e-${idx}`} fill={COLORS[(idx+2) % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[0]}}></span>
              <span className="text-slate-600">Morning</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[2]}}></span>
              <span className="text-slate-600">Evening</span>
            </div>
          </div>
        </div>

        {/* Professionals Batch Day Distribution */}
        <div className="p-4 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">BATCH DAY DISTRIBUTION — PROFESSIONALS</h3>
            <div className="text-sm text-slate-500">EnrollmentType: Junior / Senior</div>
          </div>

          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart
                data={professionalsBatchDist}
                margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="enrollmentType" tick={{fontSize:12}} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="MORNING" name="Morning" barSize={20}>
                  {professionalsBatchDist.map((entry, idx) => (
                    <Cell key={`p-m-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
                <Bar dataKey="EVENING" name="Evening" barSize={20}>
                  {professionalsBatchDist.map((entry, idx) => (
                    <Cell key={`p-e-${idx}`} fill={COLORS[(idx+2) % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[0]}}></span>
              <span className="text-slate-600">Morning</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[2]}}></span>
              <span className="text-slate-600">Evening</span>
            </div>
          </div>
        </div>

         {/* Right: Student vs Professional donut */}
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-lg font-semibold text-black">LEAD CATEGORY</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={typeBreakdown}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={80}
                  outerRadius={140}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {typeBreakdown.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'Students') color = '#0F059E';
                    else if (entry.name === 'Professionals') color = '#1B5349';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-800 rounded-full"></span>
              <span className="text-slate-600">Students</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-800 rounded-full"></span>
              <span className="text-slate-600">Professionals</span>
            </div>
          </div>
        </div>
      </div>
      {/* =========================================================== */}

      {error && <div className="text-red-500">{error}</div>}
    </div>
  );
}

export default DashboardEnrollemnts;
